import { onUnmounted, watch, type Ref } from 'vue'
import type { Channel } from 'laravel-echo'
import getMessagingEcho from '@/composables/messagingEcho'
import type { ConversationUpdatedEvent, UnreadMessageStatusUpdateEvent } from '@/types/composables/tithelyMessaging'

const CONVERSATION_UPDATED = '.conversation-updated'
const UNREAD_MESSAGE_STATUS_UPDATE = '.unread-message-status-update'

/**
 * Subscribes to Messaging's public `public-org-messaging-{orgId}` channel and calls
 * `onUpdate` whenever a `conversation-updated` or `unread-message-status-update`
 * event is received, so callers can refetch conversation/unread data (or, for
 * `conversation-updated`, patch the affected message into the query cache directly).
 *
 * The channel is shared: Echo memoizes it by name, so every caller on the same org
 * (Person Message, Inbox Lite, ...) binds onto the same instance. Cleanup only ever
 * removes this caller's own listener — `stopListening` always passes `onUpdate`
 * (a bare call unbinds every subscriber's handler for that event), and
 * `leaveChannel` is never called (it would drop every subscriber's connection).
 */
const useMessagingConversationChannel = (
    organizationId: Ref<string | undefined>,
    onUpdate: (event: ConversationUpdatedEvent | UnreadMessageStatusUpdateEvent) => void
) => {
    let channel: Channel | null = null

    const unbind = () => {
        if (channel) {
            channel.stopListening(CONVERSATION_UPDATED, onUpdate)
            channel.stopListening(UNREAD_MESSAGE_STATUS_UPDATE, onUpdate)
        }
        channel = null
    }

    const subscribe = (orgId: string) => {
        unbind()
        const channelName = `public-org-messaging-${orgId.toLowerCase().trim()}`
        channel = getMessagingEcho().channel(channelName)
        channel.listen(CONVERSATION_UPDATED, onUpdate)
        channel.listen(UNREAD_MESSAGE_STATUS_UPDATE, onUpdate)
    }

    watch(
        organizationId,
        newOrgId => {
            if (newOrgId) {
                subscribe(newOrgId)
            } else {
                unbind()
            }
        },
        { immediate: true }
    )

    onUnmounted(unbind)
}

export default useMessagingConversationChannel
